import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Translatable } from '@hrlabs/shared-types';
import { faqSaveSchema } from '@hrlabs/validation';
import { getRow, saveRow } from '../../lib/db';
import { useToast } from '../../components/Toast';
import { BilingualField } from '../../components/BilingualField';

const tr = (v: unknown): Translatable => {
  const o = (v ?? {}) as Partial<Translatable>;
  return { en: o.en ?? '', bg: o.bg ?? '' };
};

export function FaqEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [group, setGroup] = useState<'employers' | 'candidates' | 'about'>('employers');
  const [order, setOrder] = useState(1);
  const [q, setQ] = useState<Translatable>({ en: '', bg: '' });
  const [a, setA] = useState<Translatable>({ en: '', bg: '' });
  const [onHome, setOnHome] = useState(false);
  const [status, setStatus] = useState<'draft' | 'published'>('published');

  useEffect(() => {
    if (!id) return;
    getRow('faqs', id).then((r) => {
      if (r) {
        setGroup((r.group as typeof group) ?? 'employers');
        setOrder(Number(r.order ?? 1));
        setQ(tr(r.q));
        setA(tr(r.a));
        setOnHome(Boolean(r.onHome));
        setStatus(r.status === 'draft' ? 'draft' : 'published');
      }
      setLoading(false);
    });
  }, [id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = faqSaveSchema.safeParse({ group, order, q, a, onHome, status });
    if (!parsed.success) {
      notify('danger', 'Question and answer are required in both languages.');
      return;
    }
    setSaving(true);
    try {
      await saveRow('faqs', id, parsed.data);
      notify('success', 'Saved.');
      navigate('/faqs');
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;
  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 820 }}>
      <p className="mb-1"><Link to="/faqs">← FAQs</Link></p>
      <h3 className="fw-bold mb-3">{id ? 'Edit question' : 'New question'}</h3>
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <label className="form-label fw-semibold">Group</label>
          <select className="form-select" value={group} onChange={(e) => setGroup(e.target.value as typeof group)}>
            <option value="employers">For employers</option>
            <option value="candidates">For candidates</option>
            <option value="about">About HRLabs</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-semibold">Order</label>
          <input type="number" min={0} className="form-control" value={order} onChange={(e) => setOrder(Number(e.target.value))} />
        </div>
        <div className="col-md-5">
          <label className="form-label fw-semibold">Status</label>
          <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="published">published</option>
            <option value="draft">draft</option>
          </select>
        </div>
      </div>
      <BilingualField label="Question" value={q} onChange={setQ} rows={1} required />
      <BilingualField label="Answer (plain text, 40–60 words, answer-first)" value={a} onChange={setA} rows={4} required />
      <div className="form-check mb-3">
        <input className="form-check-input" type="checkbox" id="onHome" checked={onHome} onChange={(e) => setOnHome(e.target.checked)} />
        <label className="form-check-label" htmlFor="onHome">Show on homepage</label>
      </div>
      <button className="btn btn-success" disabled={saving} type="submit">{saving ? 'Saving…' : 'Save'}</button>
    </form>
  );
}
